import * as React from 'react';
import * as _ from 'lodash';
import { Editor } from 'react-draft-wysiwyg';
import {
    EditorState,
    convertToRaw,
    convertFromRaw,
    convertFromHTML,
    ContentState,
    Modifier,
    SelectionState
} from 'draft-js';
import AnswerReferenceButton from '../customButtons/AnswerReference/AnswerReferenceButton';
import { Question, MULTIMEDIA_TYPES } from '@nexxt/common/types';
import RootReference from '../customButtons/RootReference';
import { ROOT_REFERENCE } from '../../../../constants/defaultText';
import { getAccessDevMode } from '@nexxt/common/firebase/auth/userDetails';
import { getDecorators } from '../customButtons/AnswerReference/AnswerReferenceDraftEntity';
import ImageButton from '../customButtons/ImageButton';
import { getEntityRange } from 'draftjs-utils';
export interface IEditorTextProps {
    question?: Question;
    lang?: string;
    langName: string;
    placeholder?: string;
    projectID?: string;
    richTextTranslationChange: (newText: any, index: number) => void;
    removeEditor?: (index: number) => void;
    value?: any;
    defaultText?: any;
    isHTMLText?: boolean;
    emptyTextAlert?: (has: boolean) => any;
    showDynamicReference?: boolean;
    showMessageBreak?: boolean;
    showRootReference?: boolean;
    disabled: boolean;
    index?: number;
    disableAddBubble?: boolean;
    showClose?: boolean;
    addNewBubble?: (type: MULTIMEDIA_TYPES, data?) => void;
}

export default class EditorText extends React.Component<IEditorTextProps, any> {
    static defaultProps = {
        emptyTextAlert: () => {},
        showDynamicReference: true,
        showMessageBreak: true
    };
    constructor(props) {
        super(props);
        this.state = {
            savedTexts: [],
            projectQuestions: [],
            renderReady: false, // dont render until switched on
            editorState: EditorState.createEmpty(),
            showAnswerReference: false,
            answerReferenceQuestionValue: null,
            selectedEntity: { entityKey: null, offsetKey: null }
        };
    }
    rootRefereceRef: React.RefObject<RootReference> = React.createRef();
    componentDidUpdate(prevProps) {
        if (prevProps.defaultText !== this.props.defaultText) {
            this.getTextWording(true);
        }
    }
    componentDidMount() {
        this.getTextWording(false);
    }

    toggleShowAnswerReference = (
        toggle: boolean,
        answerReferenceQuestionValue: string,
        { offsetKey, entityKey }
    ) => {
        this.setState({
            showAnswerReference: toggle,
            answerReferenceQuestionValue,
            selectedEntity: { offsetKey, entityKey }
        });
    };

    getTextWording = (defaultTextChanged: boolean) => {
        this.setState({ renderReady: true });
        try {
            if (this.props.value && !defaultTextChanged) {
                this.setState(
                    {
                        editorState: EditorState.createWithContent(
                            convertFromRaw(this.props.value)
                        )
                    },
                    this.props.emptyTextAlert(false)
                );
            } else if (
                (!this.props.value && this.props.defaultText) ||
                (defaultTextChanged && this.props.defaultText)
            ) {
                let content,
                    defaultText = this.props.defaultText;
                if (this.props.index) {
                    defaultText = this.props.index !== 0 && '';
                }
                if (this.props.isHTMLText) {
                    const convertedText = convertFromHTML(defaultText);

                    content = ContentState.createFromBlockArray(
                        convertedText.contentBlocks,
                        convertedText.entityMap
                    );
                } else {
                    content = ContentState.createFromText(defaultText);
                }
                this.setState(
                    {
                        editorState: EditorState.createWithContent(content)
                    },
                    this.checkDefaulText
                );
            } else {
                this.setState(
                    {
                        editorState: EditorState.createEmpty()
                    },
                    this.props.emptyTextAlert(true)
                );
            }
        } catch (error) {
            this.setState(
                {
                    editorState: EditorState.createEmpty()
                },
                this.props.emptyTextAlert(true)
            );
        }
    };

    checkDefaulText = () => {
        const defaultText = this.props?.defaultText || '';
        if (defaultText?.includes(ROOT_REFERENCE)) {
            this.rootRefereceRef?.current.replaceText(defaultText);
        } else {
            this.addNewText();
        }
    };
    addNewText = () => {
        const newText = convertToRaw(
            this.state.editorState.getCurrentContent()
        );

        // Using just newText. By Ajay Shrestha
        if (this.props.lang) {
            this.props.richTextTranslationChange(
                {
                    language: this.props.lang,
                    richTexts: newText
                },
                this.props.index
            );
        } else {
            this.props.richTextTranslationChange(newText, this.props.index);
        }
    };

    onEditorStateChange = (editorState: EditorState) => {
        const currentText = convertToRaw(
            this.state.editorState.getCurrentContent()
        );
        const newText = convertToRaw(editorState.getCurrentContent());
        this.setState({ editorState }, () => {
            if (!_.isEqual(currentText, newText)) this.addNewText();
        });
        this.props.emptyTextAlert(!editorState.getCurrentContent().hasText());
    };

    closeButton = () => {
        const { removeEditor, index, disabled } = this.props;
        return (
            !disabled && (
                <div
                    className="option-box--close"
                    onClick={(e) => removeEditor(index)}
                >
                    <i className="nx nx-close" />
                </div>
            )
        );
    };

    /**
     * Delete a DraftJS entity within this EditorText based on its @entityKey and @offsetKey
     */
    deleteEntity = (entityKey: string, offsetKey: string): void => {
        const { editorState } = this.state;
        const blockKey = offsetKey.split('-')[0];
        const contentState = editorState.getCurrentContent();
        const { start, end } = getEntityRange(editorState, entityKey);
        const newSelection = SelectionState.createEmpty(blockKey).merge({
            focusOffset: end,
            anchorOffset: start
        });
        const newContentState = Modifier.applyEntity(
            contentState,
            newSelection,
            null
        );
        const newEditorState = EditorState.push(
            editorState,
            newContentState,
            'apply-entity'
        );

        this.onEditorStateChange(
            EditorState.push(
                newEditorState,
                Modifier.removeRange(newContentState, newSelection, 'backward'),
                'remove-range'
            )
        );
    };
    render() {
        const toolbarCustomButtons = [];
        if (this.props.showDynamicReference && !this.props.disabled) {
            toolbarCustomButtons.push(
                <AnswerReferenceButton
                    question={this.props.question}
                    editorState={this.state.editorState}
                    toggleShowAnswerReference={(toggle) =>
                        this.toggleShowAnswerReference(toggle, null, {
                            entityKey: '',
                            offsetKey: ''
                        })
                    }
                    answerReferenceQuestionValue={
                        this.state.answerReferenceQuestionValue
                    }
                    showAnswerReference={this.state.showAnswerReference}
                    selectedEntity={this.state.selectedEntity}
                    addNewBubble={this.props.addNewBubble}
                    deleteEntity={this.deleteEntity}
                    disabled={this.props.disabled}
                    langName={this.props.langName}
                    onEditorChange={(editorState) => {
                        this.setState({ editorState });
                        this.props.richTextTranslationChange(
                            convertToRaw(editorState.getCurrentContent()),
                            this.props.index
                        );
                    }}
                />
            );
        }
        if (
            this.props.showRootReference &&
            this.props.question &&
            !this.props.disabled
        ) {
            toolbarCustomButtons.push(
                <RootReference
                    ref={this.rootRefereceRef}
                    question={this.props.question}
                />
            );
        }
        const isDevAllowedAccount = getAccessDevMode();
        const devToolbarOptions = [];
        const defaultToolbarOptions = [
            'inline',
            'list',
            'colorPicker',
            'fontSize',
            'fontFamily',
            'remove',
            'history',
            'emoji',
            'link'
        ];
        const toolbarOptions = defaultToolbarOptions.concat(
            isDevAllowedAccount ? devToolbarOptions : []
        );
        const fontSizeOptions = [
            8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 36, 48, 60,
            72, 96
        ];
        const inlineStyleOptions = [
            'bold',
            'italic',
            'underline',
            'strikethrough'
        ];

        if (!this.props.disableAddBubble && !this.props.disabled) {
            toolbarCustomButtons.push(
                <ImageButton
                    addNewBubble={(type, data) =>
                        this.props.addNewBubble(type, data)
                    }
                />
            );
        }

        return (
            this.state.renderReady && (
                <React.Fragment>
                    <div className="rdw-editor-wrapper">
                        {this.props.showClose && this.closeButton()}
                        <Editor
                            className="text-editor"
                            editorState={this.state.editorState}
                            wrapperClassName="draft-wrapper"
                            editorClassName={`draft-editor ${
                                this.props.disabled ? 'disabled' : ''
                            }`}
                            toolbarClassName={`draft-toolbar ${
                                this.props.disabled ? 'disabled' : ''
                            }`}
                            stripPastedStyles={true}
                            readOnly={this.props.disabled}
                            toolbar={
                                this.props.disabled
                                    ? { options: [] }
                                    : {
                                          options: toolbarOptions,
                                          inline: {
                                              options: inlineStyleOptions
                                          },
                                          fontSize: {
                                              options: fontSizeOptions
                                          }
                                      }
                            }
                            toolbarCustomButtons={toolbarCustomButtons}
                            onEditorStateChange={this.onEditorStateChange}
                            placeholder={
                                this.props.placeholder ||
                                'Please add question texts'
                            }
                            customDecorators={[
                                getDecorators({
                                    className: '',
                                    language: this.props.langName,
                                    editorState: this.state.editorState,
                                    toggleShowAnswerReference:
                                        this.toggleShowAnswerReference,
                                    deleteEntity: this.deleteEntity,
                                    disabled: this.props.disabled
                                })
                            ]}
                            onBlur={(e) => {
                                e.preventDefault();
                            }}
                        />
                    </div>
                </React.Fragment>
            )
        );
    }
}
